/**
 * Etichetta della provenienza accanto al mittente di ogni messaggio (decisione di Davide del 2/11/2026):
 * nella conversazione, anche a messaggio chiuso, e nell'archivio delle caselle. La fascia sopra il
 * messaggio aperto (522) resta: l'etichetta dice in una parola se il messaggio viene da CIDIS o no.
 *
 * La provenienza la decide il server di ricezione (servizi/posta-provenienza.ts dell'API). Per lo
 * storico importato, arrivato prima dei controlli, non c'e': si guarda il dominio del mittente scritto
 * (i domini di `accesso.domini`, GET /auth/domini) e il titolo dice che non e' una verifica.
 *
 * Senza React e senza dipendenze: le regole sono provate in apps/api/test/posta-provenienza-etichette.test.ts.
 */

export type DatiProvenienza = {
  direzione: 'entrata' | 'uscita'
  /** Il mittente scritto, «Nome <indirizzo>» o l'indirizzo. */
  da: string
  provenienza: string | null
}

export type EtichettaProvenienza = { testo: string; tono: 'positivo' | 'attenzione' | 'critico'; titolo: string }

export const TITOLO_STORICO = 'Messaggio dello storico: provenienza non verificata dal server'

const CIDIS = (titolo: string): EtichettaProvenienza => ({ testo: 'CIDIS', tono: 'positivo', titolo })

const NOTE: Record<string, EtichettaProvenienza> = {
  interna: CIDIS('Consegnato dentro il gestionale da un collega'),
  cidis: CIDIS('Verificato dal server di ricezione: partito dai server di posta di CIDIS'),
  automatica: { testo: 'CIDIS · automatica', tono: 'positivo', titolo: 'Comunicazione automatica del gestionale CIDIS' },
  esterna: { testo: 'esterno', tono: 'attenzione', titolo: 'Viene da fuori CIDIS' },
  esterna_non_verificata: { testo: 'esterno, non verificato', tono: 'attenzione',
    titolo: 'Viene da fuori CIDIS e il dominio del mittente non conferma di averlo spedito' },
  falsa_cidis: { testo: 'falso CIDIS?', tono: 'critico',
    titolo: 'Si presenta come CIDIS ma non viene dai server di CIDIS: possibile truffa' },
}

/** Il dominio del mittente scritto, minuscolo; vuoto se non c'e' un indirizzo. */
export function dominioMittente(da: string): string {
  const dentro = /<([^>]+)>/.exec(da)
  const indirizzo = (dentro ? dentro[1]! : da).trim().toLowerCase()
  const chiocciola = indirizzo.lastIndexOf('@')
  return chiocciola > 0 ? indirizzo.slice(chiocciola + 1) : ''
}

/**
 * L'etichetta del messaggio. In uscita dalla casella e' di CIDIS. Senza provenienza (storico) decide il
 * dominio del mittente; con i domini non ancora noti (`null`) non si dice nulla, per non dire il falso.
 */
export function etichettaProvenienza(m: DatiProvenienza, domini: string[] | null): EtichettaProvenienza | null {
  if (m.direzione === 'uscita') return CIDIS('Spedito da questa casella')
  const nota = m.provenienza ? NOTE[m.provenienza] : undefined
  if (nota) return nota
  if (!domini) return null
  return domini.includes(dominioMittente(m.da))
    ? CIDIS(TITOLO_STORICO)
    : { testo: 'esterno', tono: 'attenzione', titolo: TITOLO_STORICO }
}
